import { lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { CartProvider } from "@/contexts/CartContext";
import { TrackingProvider } from "@/components/TrackingProvider";
import { ProtectedAdminRoute } from "./components/admin/ProtectedAdminRoute";
import { ErrorBoundary } from "./components/ui/error-boundary";
import { useInitFacebookPixel } from "./hooks/useInitFacebookPixel";
import { useInitClarity } from "./hooks/useInitClarity";
import { useDynamicBranding } from "./hooks/useDynamicBranding";
import { LoadingState } from "@/components/LoadingState";

import { DomainHandler } from "@/components/DomainHandler";
import { SettingsLayout } from "./features/settings/components/SettingsLayout";
import { CommandMenuProvider } from "@/components/admin/search/CommandMenuContext";
import { GlobalSearchDialog } from "@/components/admin/search/GlobalSearchDialog";
import { LanguageProvider } from "@/contexts/LanguageContext";
import { AdminLayout } from "@/components/admin/layout/AdminLayout";

// Lazy load route components for better code splitting
const Home = lazy(() => import("./features/content/pages/Home"));
const Index = lazy(() => import("./features/content/pages/Index"));
const LandingPage = lazy(() => import("./features/content/pages/LandingPageOptimized"));
const LandingPage2 = lazy(() => import("./features/content/pages/LandingPage2"));
const Admin = lazy(() => import("./features/dashboard/pages/Admin"));
const AdminLogin = lazy(() => import("./features/auth/pages/AdminLogin"));
const AdminOrders = lazy(() => import("./features/orders/pages/AdminOrders"));
const AdminProducts = lazy(() => import("./features/products/pages/AdminProducts"));
const AdminProductNew = lazy(() => import("./features/products/pages/AdminProductNew"));
const AdminMedia = lazy(() => import("./features/media/pages/AdminMedia"));
const AdminAnalyticsUnified = lazy(() => import("./features/analytics/pages/AdminAnalyticsUnified"));
const AnalyticsReportView = lazy(() => import("./features/analytics/pages/AnalyticsReportView"));
const AdminCustomLandingPages = lazy(() => import("./features/content/pages/AdminCustomLandingPages"));
const AdminPixelSettings = lazy(() => import("./features/marketing/pages/AdminPixelSettings"));
const AdminSiteSettings = lazy(() => import("./features/settings/pages/AdminSiteSettings"));
// Placeholder for missing pages
const PlaceholderPage = () => <div className="p-8 text-center text-gray-500">قريباً...</div>;
const AdminAnalyticsPro = lazy(() => import("./features/analytics/pages/AdminAnalyticsPro"));
const AdminProductAnalytics = lazy(() => import("./features/analytics/pages/AdminProductAnalytics"));
const AdminSettings = lazy(() => import("./features/settings/pages/AdminSettings")); // Uncommented
const AdminUpsell = lazy(() => import("./features/sales/pages/AdminUpsell"));
const AdminDownsell = lazy(() => import("./features/sales/pages/AdminDownsell"));
const AdminAbandonedOrders = lazy(() => import("./features/orders/pages/AdminAbandonedOrders"));
const AdminBrandSettings = lazy(() => import("./features/settings/pages/AdminBrandSettings"));
const AdminPaymentsSettings = lazy(() => import("./features/settings/pages/AdminPaymentsSettings"));
const AdminShippingSettings = lazy(() => import("./features/settings/pages/AdminShippingSettings"));
const AdminLocationsSettings = lazy(() => import("./features/settings/pages/AdminLocationsSettings"));
const AdminNotificationsSettings = lazy(() => import("./features/settings/pages/AdminNotificationsSettings"));
const AdminLanguagesSettings = lazy(() => import("./features/settings/pages/AdminLanguagesSettings"));
const AdminPoliciesSettings = lazy(() => import("./features/settings/pages/AdminPoliciesSettings"));
const AdminHomeBuilder = lazy(() => import("./features/content/pages/AdminHomeBuilder"));
const AdminProductFAQs = lazy(() => import("./features/products/pages/AdminProductFAQs"));
const AdminProductReviews = lazy(() => import("./features/products/pages/AdminProductReviews"));
const AdminABTesting = lazy(() => import("./features/marketing/pages/AdminABTesting"));
const AdminABTestingDetails = lazy(() => import("./features/marketing/pages/AdminABTestingDetails"));
const ABTestRouter = lazy(() => import("./features/marketing/pages/ABTestRouter"));
const AdminCustomLandingBuilder = lazy(() => import("./features/content/pages/AdminCustomLandingBuilder"));
const AdminDashboard = lazy(() => import("./features/dashboard/pages/AdminDashboard"));
const AdminDashboardShopify = lazy(() => import("./features/dashboard/pages/AdminDashboardShopify"));
const AdminSettingsPlaceholder = lazy(() => import("./features/settings/pages/AdminSettingsPlaceholder"));

const AdminCustomers = lazy(() => import("./features/customers/pages/AdminCustomers"));
const AdminDiscounts = lazy(() => import("./features/marketing/pages/AdminDiscounts"));
const AdminMarketing = lazy(() => import("./features/marketing/pages/AdminMarketing"));
const DashboardV3 = lazy(() => import("./features/dashboard/components/v3/DashboardV3"));
const DashboardPro = lazy(() => import("./features/dashboard/pages/DashboardPro"));
const AdvancedReports = lazy(() => import("./features/analytics/pages/AdvancedReports"));
const SubscriptionManagement = lazy(() => import("./features/settings/pages/SubscriptionManagement"));
const AdminProductPages = lazy(() => import("./features/products/pages/AdminProductPages"));
const ProductBuilder = lazy(() => import("./features/products/pages/ProductBuilder"));
const AdminBranding = lazy(() => import("./features/settings/pages/AdminBranding"));
const AdminCollections = lazy(() => import("./features/products/pages/AdminCollections"));
const AdminInventory = lazy(() => import("./features/products/pages/AdminInventory"));
const AdminShippingZones = lazy(() => import("./features/settings/pages/AdminShipping"));
const AdminPaymentGateways = lazy(() => import("./features/settings/pages/AdminPaymentGateways"));
const AdminTeam = lazy(() => import("./features/settings/pages/AdminTeam"));
const SuperAdminDashboard = lazy(() => import("./features/admin/pages/SuperAdminDashboard"));
const BillingPage = lazy(() => import("./features/settings/pages/BillingPage"));
const CustomerPortal = lazy(() => import("./features/customers/pages/CustomerPortal"));
const UnifiedPageBuilder = lazy(() => import("./features/builder/pages/ProPageBuilderUltimate"));
const AnalyticsDashboard = lazy(() => import("./features/analytics/pages/AnalyticsDashboard"));
const SettingsUnified = lazy(() => import("./features/settings/pages/SettingsUnified"));
const PagesManager = lazy(() => import("./features/pages/PagesManager"));
const RealAnalyticsDashboard = lazy(() => import("./features/analytics/pages/RealAnalyticsDashboard"));
const TemplatesGallery = lazy(() => import("./features/templates/pages/TemplatesGallery"));
const Themes = lazy(() => import("./features/content/pages/Themes"));
const ThemeEditor = lazy(() => import("./features/content/components/ThemeEditor"));
const AdminThemes = lazy(() => import("./features/content/pages/AdminThemes"));
const AdminThemeEditor = lazy(() => import("./features/content/pages/AdminThemeEditor"));
const AdminThemeNew = lazy(() => import("./features/content/pages/AdminThemeNew"));
const AdminFunnels = lazy(() => import("./features/funnels/pages/AdminFunnels"));
// New integrated Theme Editor for funnel mode
const ThemeEditorNew = lazy(() => import("./theme-engine/ThemeEditor"));

const FunnelCanvas = lazy(() => import("./features/funnels/pages/FunnelCanvas"));
const FunnelStepEditor = lazy(() => import("./features/funnels/pages/FunnelStepEditor"));
const FunnelRenderer = lazy(() => import("./features/funnels/pages/FunnelRenderer"));
const FunnelRunner = lazy(() => import("./features/funnels/pages/FunnelRunner"));
const FunnelAnalytics = lazy(() => import("./features/funnels/pages/FunnelAnalytics"));
const FunnelSetup = lazy(() => import("./features/funnels/pages/FunnelSetup"));
const AdminLandingAnalytics = lazy(() => import("./features/analytics/pages/AdminLandingAnalytics"));
const CustomLandingPage = lazy(() => import("./features/content/pages/CustomLandingPage"));
const Upsell = lazy(() => import("./features/sales/pages/Upsell"));
const Downsell = lazy(() => import("./features/sales/pages/Downsell"));
const ProductPageOptimized = lazy(() => import("./features/products/pages/ProductPageOptimized"));
const ProductPage = lazy(() => import("./features/products/pages/ProductPage"));
const Products = lazy(() => import("./features/products/pages/Products"));
const ProductDetails = lazy(() => import("./features/products/pages/ProductDetails"));
const CheckoutPage = lazy(() => import("./features/checkout/pages/CheckoutPage"));
const ThankYouPage = lazy(() => import("./features/checkout/pages/ThankYouPage"));
const Checkout = lazy(() => import("./features/checkout/pages/Checkout"));
const ThankYou = lazy(() => import("./features/checkout/pages/ThankYou"));
const NotFound = lazy(() => import("./pages/NotFound"));
const MerchantOnboarding = lazy(() => import("./features/onboarding/pages/MerchantOnboarding"));

// Enhanced QueryClient with better defaults
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

import { useRealtimeAnalytics } from "@/features/analytics/hooks/useRealtimeAnalytics";

// Component to initialize hooks that need QueryClient
const AppInitializers = () => {
  useInitFacebookPixel();
  useInitClarity();
  useDynamicBranding();
  useRealtimeAnalytics(); // Add tracking hook
  return null;
};

import { ThemeProvider } from "@/contexts/ThemeContext";

const App = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <LanguageProvider>
          <TooltipProvider>
            <CartProvider>
              <Toaster />
              <Sonner position="top-center" richColors />

              <BrowserRouter>
                <AppInitializers />
                <TrackingProvider>
                  <Suspense fallback={<LoadingState />}>
                    <DomainHandler>
                      <CommandMenuProvider>
                        <GlobalSearchDialog />
                        <Routes>
                          <Route path="/" element={<Home />} />
                          <Route path="/index" element={<Index />} />
                          <Route path="/bolt1" element={<LandingPage />} />
                          <Route path="/bolt2" element={<LandingPage2 />} />
                          <Route path="/admin/funnels/:id/analytics" element={<FunnelAnalytics />} />
                          <Route path="/admin/funnels/:id/setup" element={<FunnelSetup />} />
                          <Route path="/admin/login" element={<AdminLogin />} />
                          <Route path="/onboarding" element={<MerchantOnboarding />} />
                          <Route path="/my-account" element={<CustomerPortal />} />

                          {/* Public Funnel Routes */}
                          <Route path="/f/:funnelId" element={<FunnelRenderer />} />
                          <Route path="/f/:funnelId/:stepSlug" element={<FunnelRenderer />} />

                          <Route path="/funnel/:funnelId" element={<FunnelRunner />} />
                          <Route path="/funnel/:funnelId/step/:stepId" element={<FunnelRunner />} />

                          {/* Full Screen Builder Route */}
                          <Route path="/admin/funnels/:funnelId/editor/:stepId" element={
                            <ProtectedAdminRoute>
                              <FunnelStepEditor />
                            </ProtectedAdminRoute>
                          } />

                          {/* Integrated Theme Editor (Canvas Bridge) */}
                          <Route path="/admin/theme-editor" element={
                            <ProtectedAdminRoute>
                              <ThemeEditorNew />
                            </ProtectedAdminRoute>
                          } />

                          <Route path="/admin/funnels/new" element={
                            <ProtectedAdminRoute>
                              <FunnelCanvas />
                            </ProtectedAdminRoute>
                          } />
                          <Route path="/admin/funnels/:id" element={
                            <ProtectedAdminRoute>
                              <FunnelCanvas />
                            </ProtectedAdminRoute>
                          } />



                          {/* Home Builder Route */}
                          <Route path="/admin/home-builder" element={
                            <ErrorBoundary>
                              <ProtectedAdminRoute>
                                <AdminHomeBuilder />
                              </ProtectedAdminRoute>
                            </ErrorBoundary>
                          } />
                          <Route path="/admin/themes" element={
                            <ErrorBoundary>
                              <ProtectedAdminRoute>
                                <AdminLayout>
                                  <AdminThemes />
                                </AdminLayout>
                              </ProtectedAdminRoute>
                            </ErrorBoundary>
                          } />
                          <Route path="/admin/themes/new" element={
                            <ProtectedAdminRoute>
                              <AdminThemeNew />
                            </ProtectedAdminRoute>
                          } />
                          <Route path="/admin/themes/edit/:id" element={
                            <ProtectedAdminRoute>
                              <AdminThemeEditor />
                            </ProtectedAdminRoute>
                          } />

                          {/* Admin Routes with New Layout */}
                          <Route path="/admin" element={<ProtectedAdminRoute><AdminLayout /></ProtectedAdminRoute>}>
                            <Route index element={<Navigate to="/admin/dashboard" replace />} />

                            <Route path="discount-codes" element={<AdminDiscounts />} />
                            <Route path="dashboard-v3" element={<DashboardV3 />} />
                            <Route path="dashboard" element={<AdminDashboardShopify />} />
                            <Route path="customers" element={<AdminCustomers />} />
                            <Route path="orders" element={
                              <ErrorBoundary>
                                <AdminOrders />
                              </ErrorBoundary>
                            } />
                            <Route path="products" element={<AdminProducts />} />
                            <Route path="products/new" element={<AdminProductNew />} />
                            <Route path="collections" element={<AdminCollections />} />
                            <Route path="inventory" element={<AdminInventory />} />
                            <Route path="media" element={<AdminMedia />} />
                            <Route path="funnels" element={<AdminFunnels />} />

                            {/* New Admin Routes */}
                            <Route path="analytics" element={<AdminAnalyticsUnified />} />
                            <Route path="analytics/report/:reportId" element={<AnalyticsReportView />} />
                            <Route path="analytics/reports" element={<AdvancedReports />} />
                            <Route path="dashboard-pro" element={<DashboardPro />} />
                            <Route path="marketing" element={<AdminMarketing />} />
                            <Route path="pixels" element={<AdminPixelSettings />} />
                            <Route path="settings" element={<AdminSettings />} />

                            {/* Shopify-like Routes */}
                            <Route path="shipping" element={<AdminShippingZones />} />
                            <Route path="payment-gateways" element={<AdminPaymentGateways />} />
                            <Route path="team" element={<AdminTeam />} />
                            <Route path="super-admin" element={<SuperAdminDashboard />} />
                            <Route path="billing" element={<BillingPage />} />

                            {/* New Pro Builder & Unified Pages */}
                            <Route path="page-builder" element={<UnifiedPageBuilder mode="home" />} />
                            <Route path="pro-builder" element={<UnifiedPageBuilder mode="landing" />} />
                            <Route path="pages" element={<PagesManager />} />
                            <Route path="analytics-pro" element={<AnalyticsDashboard />} />
                            <Route path="analytics-real" element={<RealAnalyticsDashboard />} />
                            <Route path="templates" element={<TemplatesGallery />} />
                            <Route path="settings-pro" element={<SettingsUnified />} />

                            {/* Settings Routes */}
                            <Route path="settings/payments" element={<AdminPaymentsSettings />} />
                            <Route path="settings/shipping" element={<AdminShippingSettings />} />
                            <Route path="settings/locations" element={<AdminLocationsSettings />} />
                            <Route path="settings/notifications" element={<AdminNotificationsSettings />} />
                            <Route path="settings/languages" element={<AdminLanguagesSettings />} />
                            <Route path="settings/policies" element={<AdminPoliciesSettings />} />
                            <Route path="settings/branding" element={<AdminBrandSettings />} />
                            <Route path="settings/subscriptions" element={<SubscriptionManagement />} />

                            {/* Fallback for implemented but not yet routed or missing settings */}
                            <Route path="settings/:section" element={<AdminSettingsPlaceholder />} />
                            <Route path="content" element={<AdminCustomLandingPages />} />
                            <Route path="store" element={<AdminSiteSettings />} />

                            <Route path="apps" element={<PlaceholderPage />} />
                          </Route>
                          <Route path="/ab/:experimentId" element={<ABTestRouter />} />
                          <Route path="/lp/:slug" element={<CustomLandingPage />} />
                          <Route path="/upsell" element={<Upsell />} />
                          <Route path="/downsell" element={<Downsell />} />
                          <Route path="/bolt3" element={<ProductPageOptimized />} />
                          <Route path="/products" element={<Products />} />
                          <Route path="/product/:id" element={<ProductDetails />} />
                          <Route path="/shop/checkout" element={<CheckoutPage />} />
                          <Route path="/shop/thank-you" element={<ThankYouPage />} />
                          <Route path="/checkout" element={<Checkout />} />
                          <Route path="/thank-you" element={<ThankYou />} />
                          {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
                          <Route path="*" element={<NotFound />} />
                        </Routes>
                      </CommandMenuProvider>
                    </DomainHandler>
                  </Suspense>
                </TrackingProvider>
              </BrowserRouter>
            </CartProvider>
          </TooltipProvider>
        </LanguageProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
};
export default App;